import { useState } from "react";
import { Heart, ImagePlus, Loader2, X, Trash2 } from "lucide-react";
import { api, errText, photoUrl } from "../lib/api";
import { compressImage } from "../lib/image";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { toast } from "sonner";

export function ReunionStoryCard({ listing, onChange }) {
  const { t } = useLanguage();
  const existing = listing.reunion_story;
  const [photo, setPhoto] = useState(existing?.photo || "");
  const [note, setNote] = useState(existing?.note || "");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  const onFile = async (e) => {
    const file = (e.target.files || [])[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", await compressImage(file));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(data.path);
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!photo && !note.trim()) return toast.error(t("rs_need"));
    setBusy(true);
    try {
      const { data } = await api.put(`/listings/${listing.id}/reunion-story`, { photo, note });
      onChange(data);
      toast.success(t("rs_saved"));
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/listings/${listing.id}/reunion-story`);
      onChange(null); setPhoto(""); setNote("");
      toast.success(t("rs_removed"));
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };

  return (
    <section className="bg-white rounded-2xl border-2 border-emerald-200 p-6" data-testid="reunion-story-card">
      <h3 className="font-display font-bold text-[#12234A] flex items-center gap-2"><Heart size={18} className="text-emerald-600 fill-emerald-600" /> {existing ? t("rs_edit_title") : t("rs_title")}</h3>
      <p className="text-sm text-slate-500 mt-0.5">{t("rs_sub")}</p>
      <div className="mt-4 grid sm:grid-cols-[140px_1fr] gap-4">
        <div>
          <Label>{t("rs_photo")}</Label>
          {photo ? (
            <div className="relative mt-1 h-32 w-full sm:w-[140px] rounded-xl overflow-hidden border border-slate-200">
              <img src={photoUrl(photo)} alt="" className="h-full w-full object-cover" data-testid="reunion-photo-preview" />
              <button type="button" onClick={() => setPhoto("")} data-testid="reunion-photo-remove" className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center"><X size={13} /></button>
            </div>
          ) : (
            <label data-testid="reunion-photo-upload" className="mt-1 h-32 w-full sm:w-[140px] rounded-xl border-2 border-dashed border-emerald-300 flex flex-col items-center justify-center gap-1 cursor-pointer text-emerald-600 hover:bg-emerald-50 transition-colors">
              {uploading ? <Loader2 size={22} className="animate-spin" /> : <ImagePlus size={22} />}
              <span className="text-xs font-semibold">{t("rs_photo")}</span>
              <input type="file" accept="image/*" onChange={onFile} className="hidden" />
            </label>
          )}
        </div>
        <div>
          <Label>{t("rs_note")}</Label>
          <Textarea value={note} maxLength={300} onChange={(e) => setNote(e.target.value)} rows={4} placeholder={t("rs_note_ph")} data-testid="reunion-note-input" className="mt-1 rounded-xl" />
          <p className="text-[11px] text-slate-400 text-right mt-0.5" data-testid="reunion-note-count">{note.length}/300</p>
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={save} disabled={busy || uploading} data-testid="reunion-save-btn" className="rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
          {busy ? <Loader2 size={16} className="animate-spin" /> : t("rs_save")}
        </Button>
        {existing && (
          <Button variant="outline" onClick={remove} disabled={busy} data-testid="reunion-remove-btn" className="rounded-xl gap-1.5 text-red-600 hover:text-red-700"><Trash2 size={15} /> {t("rs_remove")}</Button>
        )}
      </div>
    </section>
  );
}
